import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    User, Upload, FileSignature, CheckCircle2, AlertTriangle,
    ShieldCheck, Loader2, ChevronRight, CreditCard, Camera,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUB = `${BACKEND}/api/checkin`;

const STEPS = [
    { key: "details", label: "Your details" },
    { key: "document", label: "ID / Passport" },
    { key: "policies", label: "Policies & signature" },
    { key: "payment", label: "Payment" },
    { key: "done",    label: "Confirmation" },
];

export default function PublicCheckIn() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [step, setStep] = useState(0);
    const [error, setError] = useState(null);

    const load = async () => {
        try {
            const r = await axios.get(`${PUB}/${token}`);
            setData(r.data);
            if (r.data.checkin.status === "submitted" || r.data.checkin.status === "verified") {
                setStep(4);
            }
        } catch (e) {
            setError(e.response?.data?.detail || "Invalid or expired check-in link");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    if (error) {
        return (
            <Shell>
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                    <AlertTriangle className="mx-auto mb-3 text-red-500" size={36} />
                    <div className="text-base font-semibold text-slate-900">Link unavailable</div>
                    <div className="mt-1 text-sm text-slate-500">{error}</div>
                </div>
            </Shell>
        );
    }
    if (!data) {
        return <Shell><div className="text-center text-sm text-slate-500">Loading…</div></Shell>;
    }

    const requirePayment = data.require_payment && data.reservation.balance > 0;
    const visibleSteps = requirePayment ? STEPS : STEPS.filter((s) => s.key !== "payment");

    return (
        <Shell hotel={data.hotel}>
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Online check-in</div>
                    <div className="mt-1 text-xl font-semibold text-slate-900" data-testid="checkin-title">
                        Welcome, {data.reservation.guest_first_name || "guest"}
                    </div>
                    <div className="mt-0.5 text-[12px] text-slate-500">
                        {data.reservation.check_in} → {data.reservation.check_out} · {data.reservation.guests || 1} guest(s)
                    </div>
                </div>
                <div className="hidden sm:block">
                    <Stepper steps={visibleSteps} active={step} />
                </div>
            </div>

            {step === 0 && <DetailsStep token={token} data={data} onDone={() => { setStep(1); load(); }} />}
            {step === 1 && <DocumentStep token={token} onDone={() => setStep(2)} onPrev={() => setStep(0)} />}
            {step === 2 && <PoliciesStep token={token} data={data} onDone={() => setStep(requirePayment ? 3 : 4)} onPrev={() => setStep(1)} onSubmitted={load} />}
            {step === 3 && <PaymentStep token={token} balance={data.reservation.balance} currency={data.hotel.currency} onDone={() => setStep(4)} />}
            {step === 4 && <DoneStep data={data} />}
        </Shell>
    );
}

// ─────────────────────────────────────────────────────────────
// Steps
// ─────────────────────────────────────────────────────────────
function DetailsStep({ token, data, onDone }) {
    const pre = data.checkin.personal_details || {};
    const [f, setF] = useState({
        first_name: pre.first_name || data.reservation.guest_first_name || "",
        last_name: pre.last_name || data.reservation.guest_last_name || "",
        email: pre.email || "", phone: pre.phone || "",
        address: pre.address || "", city: pre.city || "", country: pre.country || "",
        companion_count: pre.companion_count || 0, notes: pre.notes || "",
        arrival_time: data.checkin.arrival_time || "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true);
        setErr(null);
        try {
            await axios.post(`${PUB}/${token}/details`, f);
            onDone();
        } catch (e) { setErr(e.response?.data?.detail || "Could not save"); }
        finally { setBusy(false); }
    };
    return (
        <Card icon={<User size={16} />} title="Your details">
            <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="checkin-details-form">
                <Field label="First name" required><input required value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="ci-first" /></Field>
                <Field label="Last name" required><input required value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="ci-last" /></Field>
                <Field label="Email"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
                <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
                <Field label="Address" wide><input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
                <Field label="City"><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
                <Field label="Country"><input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></Field>
                <Field label="Arrival time" required><input type="time" required value={f.arrival_time} onChange={(e) => setF({ ...f, arrival_time: e.target.value })} data-testid="ci-arrival" /></Field>
                <Field label="Additional guests"><input type="number" min="0" value={f.companion_count} onChange={(e) => setF({ ...f, companion_count: parseInt(e.target.value, 10) || 0 })} /></Field>
                <Field label="Notes (optional)" wide><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
                {err && <div className="sm:col-span-2 rounded-md border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">{err}</div>}
                <div className="sm:col-span-2 mt-2 flex justify-end">
                    <button type="submit" disabled={busy} className="primary" data-testid="ci-details-next">
                        {busy ? <Loader2 className="animate-spin" size={14} /> : <>Continue <ChevronRight size={14} /></>}
                    </button>
                </div>
            </form>
        </Card>
    );
}

function DocumentStep({ token, onDone, onPrev }) {
    const [busy, setBusy] = useState(false);
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState(null);
    const fileRef = useRef(null);

    const onFile = async (file) => {
        if (!file) return;
        setBusy(true);
        setErr(null);
        try {
            const b64 = await new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(r.result);
                r.onerror = reject;
                r.readAsDataURL(file);
            });
            const res = await axios.post(`${PUB}/${token}/document`, {
                file_name: file.name, image_b64: b64,
            });
            setDoc({ id: res.data.document_id, ...res.data.extracted });
        } catch (e) {
            setErr(e.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    const saveFields = async () => {
        try {
            await axios.patch(`${PUB}/${token}/document/${doc.id}`, doc);
            onDone();
        } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    };

    return (
        <Card icon={<Camera size={16} />} title="Upload ID or passport">
            {!doc ? (
                <div className="space-y-3" data-testid="checkin-doc-upload">
                    <button onClick={() => fileRef.current?.click()}
                            className="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-slate-500 transition hover:border-[#ff7b02] hover:text-[#ff7b02]"
                            data-testid="ci-doc-pick">
                        <Upload size={28} className="mb-2" />
                        <div className="text-sm font-medium">Click to upload a photo of your ID or passport</div>
                        <div className="mt-1 text-[11px] text-slate-400">JPEG, PNG · up to 10MB · We extract fields automatically</div>
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" hidden
                           onChange={(e) => onFile(e.target.files?.[0])} data-testid="ci-doc-input" />
                    {busy && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="animate-spin" size={14} /> Scanning document…</div>}
                    {err && <div className="rounded-md border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">{err}</div>}
                </div>
            ) : (
                <div className="space-y-4" data-testid="checkin-doc-fields">
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11.5px] text-amber-900">
                        We extracted the following fields. <strong>Please review and edit if needed</strong> before continuing.
                        Confidence: {Math.round((doc.confidence || 0) * 100)}%
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="Document type"><select value={doc.doc_type || "passport"} onChange={(e) => setDoc({ ...doc, doc_type: e.target.value })}>
                            <option value="passport">Passport</option>
                            <option value="id_card">ID Card</option>
                            <option value="drivers_license">Driver's License</option>
                        </select></Field>
                        <Field label="Document number"><input value={doc.document_number || ""} onChange={(e) => setDoc({ ...doc, document_number: e.target.value })} data-testid="ci-doc-num" /></Field>
                        <Field label="First name"><input value={doc.first_name || ""} onChange={(e) => setDoc({ ...doc, first_name: e.target.value })} data-testid="ci-doc-first" /></Field>
                        <Field label="Last name"><input value={doc.last_name || ""} onChange={(e) => setDoc({ ...doc, last_name: e.target.value })} data-testid="ci-doc-last" /></Field>
                        <Field label="Date of birth"><input type="date" value={doc.date_of_birth || ""} onChange={(e) => setDoc({ ...doc, date_of_birth: e.target.value })} /></Field>
                        <Field label="Nationality"><input value={doc.nationality || ""} onChange={(e) => setDoc({ ...doc, nationality: e.target.value })} /></Field>
                        <Field label="Expiry date"><input type="date" value={doc.expiry_date || ""} onChange={(e) => setDoc({ ...doc, expiry_date: e.target.value })} /></Field>
                    </div>
                    <div className="flex items-center justify-between">
                        <button onClick={onPrev} className="ghost">Back</button>
                        <div className="flex gap-2">
                            <button onClick={() => { setDoc(null); fileRef.current.value = ""; }} className="ghost">Re-scan</button>
                            <button onClick={saveFields} className="primary" data-testid="ci-doc-confirm">
                                Confirm <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
}

function PoliciesStep({ token, data, onDone, onPrev, onSubmitted }) {
    const [accepted, setAccepted] = useState(data.checkin.policies_accepted || false);
    const canvasRef = useRef(null);
    const [hasSig, setHasSig] = useState(!!data.checkin.signature_data_url);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.strokeStyle = "#0F172A";
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";

        let drawing = false;
        let last = null;
        const pos = (e) => {
            const r = c.getBoundingClientRect();
            const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
            const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
            return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
        };
        const start = (e) => { e.preventDefault(); drawing = true; last = pos(e); };
        const move = (e) => {
            if (!drawing) return;
            e.preventDefault();
            const p = pos(e);
            ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke();
            last = p;
            setHasSig(true);
        };
        const end = () => { drawing = false; last = null; };
        c.addEventListener("mousedown", start); c.addEventListener("mousemove", move);
        c.addEventListener("mouseup", end); c.addEventListener("mouseleave", end);
        c.addEventListener("touchstart", start); c.addEventListener("touchmove", move);
        c.addEventListener("touchend", end);
        return () => {
            c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move);
            c.removeEventListener("mouseup", end); c.removeEventListener("mouseleave", end);
            c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move);
            c.removeEventListener("touchend", end);
        };
    }, []);

    const clear = () => {
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, c.width, c.height);
        setHasSig(false);
    };

    const submit = async () => {
        if (!accepted) return setErr("You must accept the policies");
        if (!hasSig && data.require_signature) return setErr("Please sign before continuing");
        setBusy(true); setErr(null);
        try {
            const sig = canvasRef.current.toDataURL("image/png");
            await axios.post(`${PUB}/${token}/sign`, { signature_data_url: sig, policies_accepted: true });
            await axios.post(`${PUB}/${token}/complete`);
            await onSubmitted?.();
            onDone();
        } catch (e) { setErr(e.response?.data?.detail || "Submission failed"); }
        finally { setBusy(false); }
    };

    return (
        <Card icon={<FileSignature size={16} />} title="Policies & signature">
            <div className="space-y-4">
                <div className="max-h-[180px] overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[12.5px] leading-relaxed text-slate-700" data-testid="ci-policies-text">
                    {data.policies_text}
                </div>
                <label className="flex items-start gap-2 text-[13px]">
                    <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                           data-testid="ci-policies-accept" />
                    I have read and accept the hotel policies above
                </label>
                <div>
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Sign with your finger or mouse</div>
                    <canvas ref={canvasRef} width={640} height={180}
                            data-testid="ci-signature-canvas"
                            className="block w-full touch-none rounded-md border border-slate-300 bg-white" />
                    <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                        <span>{hasSig ? "Signature captured" : "Empty"}</span>
                        <button onClick={clear} className="underline">Clear</button>
                    </div>
                </div>
                {err && <div className="rounded-md border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">{err}</div>}
                <div className="flex items-center justify-between">
                    <button onClick={onPrev} className="ghost">Back</button>
                    <button onClick={submit} disabled={busy} className="primary" data-testid="ci-submit">
                        {busy ? <Loader2 className="animate-spin" size={14} /> : <>Submit check-in <ChevronRight size={14} /></>}
                    </button>
                </div>
            </div>
        </Card>
    );
}

function PaymentStep({ balance, currency, onDone }) {
    return (
        <Card icon={<CreditCard size={16} />} title={`Pay balance: ${balance} ${currency}`}>
            <div className="space-y-3 text-[13px] text-slate-600">
                <p>Your reservation has an outstanding balance. You can settle it now to skip the front-desk step.</p>
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11.5px]">
                    Online payments are currently in demo mode for this hotel. The hotel will collect at arrival or send a separate payment link.
                </p>
                <button className="primary" onClick={onDone}>Continue to confirmation</button>
            </div>
        </Card>
    );
}

function DoneStep({ data }) {
    const isVerified = data.checkin.status === "verified";
    return (
        <Card icon={<CheckCircle2 size={16} />} title={isVerified ? "Check-in verified" : "Check-in submitted"}>
            <div className="space-y-3 text-center" data-testid="ci-done">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                    <CheckCircle2 className="text-emerald-600" size={32} />
                </div>
                <div className="text-base font-semibold text-slate-900">Thank you!</div>
                <div className="text-[13px] text-slate-500">
                    {isVerified
                        ? "Reception has verified your details. See you soon!"
                        : "Reception will verify your documents before arrival."}
                </div>
            </div>
        </Card>
    );
}

// ─────────────────────────────────────────────────────────────
// Shell + primitives
// ─────────────────────────────────────────────────────────────
function Shell({ hotel, children }) {
    return (
        <div className="min-h-screen bg-slate-50 px-4 py-8 font-[Inter,sans-serif]">
            <div className="mx-auto max-w-2xl">
                <div className="mb-6 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#ff7b02] text-white text-sm font-bold">
                        {hotel?.name?.[0]?.toUpperCase() || "A"}
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-900">{hotel?.name || "Akomoda"}</div>
                        <div className="text-[11px] text-slate-500">Online check-in · Secure</div>
                    </div>
                    <div className="ml-auto flex items-center gap-1 text-[11px] text-emerald-700">
                        <ShieldCheck size={12} /> Encrypted
                    </div>
                </div>
                {children}
                <style>{`
                    .akm-checkin-card input, .akm-checkin-card select, .akm-checkin-card textarea {
                        width: 100%; padding: 0.45rem 0.6rem; font-size: 13px;
                        border: 1px solid #CBD5E1; border-radius: 6px; background: #fff;
                        outline: none; transition: border .12s;
                    }
                    .akm-checkin-card input:focus, .akm-checkin-card select:focus, .akm-checkin-card textarea:focus {
                        border-color: #ff7b02;
                    }
                    .akm-checkin-card button.primary {
                        display: inline-flex; align-items: center; gap: 6px;
                        background: #ff7b02; color: white; padding: 0.55rem 1rem;
                        border-radius: 6px; font-size: 12.5px; font-weight: 600;
                    }
                    .akm-checkin-card button.primary:hover { background: #0c5d57; }
                    .akm-checkin-card button.primary:disabled { opacity: 0.6; }
                    .akm-checkin-card button.ghost {
                        font-size: 12.5px; padding: 0.55rem 0.9rem; color: #475569;
                        border: 1px solid #CBD5E1; border-radius: 6px;
                    }
                `}</style>
            </div>
        </div>
    );
}

function Card({ icon, title, children }) {
    return (
        <div className="akm-checkin-card rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-slate-700">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#ff7b02]/10 text-[#ff7b02]">{icon}</div>
                <div className="text-[13.5px] font-semibold tracking-tight text-slate-900">{title}</div>
            </div>
            {children}
        </div>
    );
}

function Field({ label, required, wide, children }) {
    return (
        <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                {label}{required && <span className="text-red-500"> *</span>}
            </div>
            {children}
        </label>
    );
}

function Stepper({ steps, active }) {
    return (
        <ol className="flex items-center gap-1">
            {steps.map((s, i) => (
                <li key={s.key} className="flex items-center gap-1">
                    <div className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                        i < active ? "bg-emerald-500 text-white" :
                        i === active ? "bg-[#ff7b02] text-white" : "bg-slate-200 text-slate-500"
                    }`}>{i + 1}</div>
                    <span className={`text-[10px] uppercase tracking-wider ${
                        i === active ? "text-[#ff7b02] font-semibold" : "text-slate-500"
                    }`}>{s.label}</span>
                    {i < steps.length - 1 && <span className="mx-1 text-slate-300">·</span>}
                </li>
            ))}
        </ol>
    );
}
